import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  TablePagination,
  CircularProgress,
  Button,
  Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';

// Interface for contacts - similar to the one in ContactList component
interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  title?: string;
  tags?: string[];
  engagementScore?: number;
  lastContactDate?: Date;
  source?: string;
  createdAt?: Date;
  consentStatus?: 'granted' | 'denied' | 'unknown';
}

interface ContactListStepProps {
  contacts: Contact[];
  selectedContacts: string[];
  onContactsChange: (selectedIds: string[]) => void;
  loading?: boolean;
  preselectedContactIds?: string[];
  onRefresh?: () => Promise<void>;
  error?: string | null;
}

const ContactListStep: React.FC<ContactListStepProps> = ({
  contacts,
  selectedContacts,
  onContactsChange,
  loading = false,
  preselectedContactIds = [],
  onRefresh,
  error
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>(contacts);

  // Initialize with preselected contacts if provided
  useEffect(() => {
    if (preselectedContactIds.length > 0 && selectedContacts.length === 0) {
      onContactsChange(preselectedContactIds);
    }
  }, [preselectedContactIds, selectedContacts.length, onContactsChange]);

  // Filter contacts when search query changes
  useEffect(() => {
    const filtered = contacts.filter(contact => {
      const fullName = `${contact.firstName} ${contact.lastName}`.toLowerCase();
      const email = (contact.email || '').toLowerCase();
      const company = (contact.company || '').toLowerCase();
      const query = searchQuery.toLowerCase();

      return fullName.includes(query) || email.includes(query) || company.includes(query);
    });
    
    setFilteredContacts(filtered);
    setPage(0); // Reset to first page when filtering
  }, [contacts, searchQuery]);

  // Handle pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle checkbox selection
  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelectedIds = filteredContacts.map(contact => contact.id);
      onContactsChange(newSelectedIds);
      return;
    }
    onContactsChange([]);
  };

  const handleSelectClick = (event: React.MouseEvent<unknown>, id: string) => {
    const selectedIndex = selectedContacts.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = [...selectedContacts, id];
    } else if (selectedIndex === 0) {
      newSelected = selectedContacts.slice(1);
    } else if (selectedIndex === selectedContacts.length - 1) {
      newSelected = selectedContacts.slice(0, -1);
    } else if (selectedIndex > 0) {
      newSelected = [
        ...selectedContacts.slice(0, selectedIndex),
        ...selectedContacts.slice(selectedIndex + 1),
      ];
    }

    onContactsChange(newSelected);
  };

  const isSelected = (id: string) => selectedContacts.indexOf(id) !== -1;

  // Pagination logic
  const emptyRows = rowsPerPage - Math.min(rowsPerPage, filteredContacts.length - page * rowsPerPage);
  const visibleContacts = filteredContacts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Select Contacts
      </Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          sx={{ flexGrow: 1, mr: 2 }}
          margin="normal"
          placeholder="Search contacts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
        
        {onRefresh && (
          <Button 
            startIcon={<RefreshIcon />} 
            onClick={onRefresh}
            disabled={loading}
            sx={{ mt: 2, height: 40 }}
          >
            Refresh
          </Button>
        )}
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableContainer>
            <Table aria-labelledby="tableTitle" size="medium">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedContacts.length > 0 && selectedContacts.length < filteredContacts.length}
                      checked={filteredContacts.length > 0 && selectedContacts.length === filteredContacts.length}
                      onChange={handleSelectAllClick}
                    />
                  </TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Company</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleContacts.map((contact) => {
                  const isItemSelected = isSelected(contact.id);
                  
                  return (
                    <TableRow
                      hover
                      onClick={(event) => handleSelectClick(event, contact.id)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={contact.id}
                      selected={isItemSelected}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={isItemSelected} />
                      </TableCell>
                      <TableCell component="th" scope="row">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar
                            sx={{ mr: 2, bgcolor: 'primary.main' }}
                            alt={`${contact.firstName} ${contact.lastName}`}
                          >
                            {contact.firstName?.[0]}{contact.lastName?.[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body1">
                              {contact.firstName} {contact.lastName}
                            </Typography>
                            {contact.title && (
                              <Typography variant="body2" color="textSecondary">
                                {contact.title}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{contact.email}</TableCell>
                      <TableCell>{contact.company || '-'}</TableCell>
                    </TableRow>
                  );
                })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={4} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredContacts.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
      
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1">
          {selectedContacts.length} contacts selected
        </Typography>
      </Box>
    </Box>
  );
};

export default ContactListStep;
